import MsgReader from '@kenjiuno/msgreader';
import { Buffer } from 'buffer';
import { decode, ReadedEmlJson, readEml } from 'eml-parse-js';
import { BehaviorSubject, catchError, concatMap, delay, finalize, forkJoin, from, map, mergeMap, NEVER, Observable, of, switchMap, take, takeUntil, tap, throwError, toArray } from 'rxjs';
import { cleanAndTruncateFileName, convertFileToBuffer, MAX_FILENAME_LENGTH } from './file-utils';

export interface OutlookContentResult<A, B> {
  detail: A;
  value: B;
}

/**
 * Enhanced version of the `Word.RequestContext` type.
 * Word API doesn't have concept of an ID or a name for the file.
 *
 * the URL is unique for the file and can be used as an ID.
 * and the filename is the last part of the URL.
 *
 *  (only have values if the document is saved)
 */
export type WordRequestContext = Word.RequestContext & {
  // URL of the document in OneDrive or SharePoint
  url: string;
  // Name of the document
  fileName: string;
}

export type OutlookItemType = Office.Item &
  Office.ItemCompose &
  Office.ItemRead &
  Office.Message &
  Office.MessageCompose &
  Office.MessageRead &
  Office.Appointment &
  Office.AppointmentCompose &
  Office.AppointmentRead;

export function getAttachmentContentAsyncObservable(
  item: OutlookItemType | Office.LoadedMessageRead,
  att: Office.AttachmentDetails
): Observable<OutlookContentResult<Office.AttachmentDetails, Office.AttachmentContent>> {
  return new Observable((observer) => {
    item.getAttachmentContentAsync(att.id, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        // outlook sometimes does not report correct success.. so lets test.
        if (result.value.content === '' && att.size > 0) {
          observer.error(new Error(`Failed to get attachment content.  Outlook returned empty content for ${att.size} byte attachment`));
        } else {
          observer.next({ detail: att, value: result.value });
          observer.complete();
        }
      } else {
        console.error(result.error);
        observer.error(new Error('Failed to get attachment content'));
      }
    });
  });
}

export function getAsFileAsyncObservable(item: OutlookItemType | Office.LoadedMessageRead): Observable<OutlookContentResult<string, string>> {
  return new Observable((observer) => {
    item.getAsFileAsync({}, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        observer.next({ detail: item.itemId, value: result.value });
        observer.complete();
      } else {
        console.error(result.error);
        observer.error(new Error('Failed to get file'));
      }
    });
  });
}

export function getFilePropertiesObservable() {
  return createObservableFromAsyncResult<Office.FileProperties>(Office.context.document.getFilePropertiesAsync);
}

// get word context as observable
export function getClassicWordContext(): Observable<Word.RequestContext> {
  return new Observable((observer) => {
    Word.run(async (context: Word.RequestContext) => {
      // load the document properties
      const builtInProperties: Word.DocumentProperties = context.document.properties;
      builtInProperties.load("*");
      context.document.load("*");
      await context.sync();

      observer.next(context);
      observer.complete();
    }).catch((error) => {
      console.error('Error getting Word context:', error);
      observer.error(error);
    });
  });
}

/**
 * Get an enhanced version of the `Word.RequestContext` type that contains the URL and filename.
 *
 * @returns
 */
export function getWordContext(): Observable<WordRequestContext> {
  return forkJoin([
    getClassicWordContext(),
    getFilePropertiesObservable(),
  ]).pipe(map(([context, fileProperties]) => {
    Object.assign(context as WordRequestContext, {
      url: fileProperties.url,
      fileName: fileProperties.url?.split('/').pop() ?? ''
    });
    return context as WordRequestContext;
  }));
}

/**
 * Downloads the current document as a compressed file (docx, pptx, xlsx) using Office.js API.
 *
 * @returns An Observable that emits the file content as a Blob.
 */
export function getDocumentAsCompressedObservable(): Observable<Blob> {
  return new Observable((observer) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 1048576 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const file = result.value;
          const docdataSlices: Uint8Array[] = [];
          let slicesReceived = 0;

          const getSlice = (sliceIndex: number) => {
            file.getSliceAsync(sliceIndex, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
                docdataSlices[sliceResult.value.index] = sliceResult.value.data;
                slicesReceived++;
                if (slicesReceived === file.sliceCount) {
                  file.closeAsync();
                  observer.next(mergeSlices(docdataSlices));
                  observer.complete();
                } else {
                  getSlice(sliceIndex + 1);
                }
              } else {
                file.closeAsync();
                observer.error(new Error(`getSliceAsync Error: ${sliceResult.error.message}`));
              }
            });
          };

          getSlice(0);
        } else {
          observer.error(new Error(`getFileAsync Error: ${result.error.message}`));
        }
      }
    );
  });
}

/**
 * Merges an array of Uint8Array slices into a single Uint8Array.
 * @param slices The array of Uint8Array slices.
 * @returns A Blob containing the merged data.
 */
function mergeSlices(slices: Uint8Array[]): Blob {
  const totalLength = slices.reduce((sum, slice) => sum + slice.length, 0);
  const mergedArray = new Uint8Array(totalLength);
  let offset = 0;

  for (const slice of slices) {
    mergedArray.set(slice, offset);
    offset += slice.length;
  }
  return new Blob([mergedArray]);
}

export function createObservableFromAsyncResult<T>(asyncCall: (callback: (result: Office.AsyncResult<T>) => void) => void): Observable<T> {
  return new Observable<T>((observer) => {
    try {
      asyncCall((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          observer.error(result.error);
        } else {
          observer.next(result.value);
          observer.complete();
        }
      });
    } catch (error) {
      observer.error(error);
    }
  });
}


export type MailItemMode = 'read' | 'compose';

export type OutlookAttContentResult = OutlookContentResult<Office.AttachmentDetails, Office.AttachmentContent | undefined> & { success: boolean, error?: unknown };

export type OutlookContentType = 'eml' | 'att';
export type OutlookSharedDetail = { type: OutlookContentType, id: string };
export type OutlookSharedContentResult = OutlookContentResult<OutlookSharedDetail, Office.AttachmentContent | undefined> & { success: boolean, error?: unknown };

/**
 * Abstract class representing a mail item.
 *
 * @abstract
 */
export abstract class MailItem {
  /**
   * The unique identifier of the mail item.
   * @type {string | null}
   */
  itemId: string | null;

  /**
   * The subject of the mail item.
   * @type {string}
   */
  subject: string;

  /**
   * The date and time when the mail item was created.
   * @type {Date | null}
   */
  dateTimeCreated: Date | null;

  /**
   * The attachments associated with the mail item.
   * @type {Office.AttachmentDetails[]}
   */
  attachments: Office.AttachmentDetails[];

  /**
   * The name of the file associated with the mail item.
   */
  fileName: string;

  /**
   * The mode of the mail item.
   * @protected
   * @type {MailItemMode}
   */
  protected _mode: MailItemMode;

  /**
   * Creates an instance of MailItem.
   *
   * @param {string | null} itemId - The unique identifier of the mail item.
   * @param {string} subject - The subject of the mail item.
   * @param {Date | null} dateTimeCreated - The date and time when the mail item was created.
   * @param {Office.AttachmentDetails[]} attachments - The attachments associated with the mail item.
   * @param {MailItemMode} mode - The mode of the mail item.
   */
  protected constructor(
    itemId: string | null,
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    mode: MailItemMode,
    fileName: string
  ) {
    this.itemId = itemId;
    this.subject = subject;
    this.dateTimeCreated = dateTimeCreated;
    this.attachments = attachments;
    this.fileName = fileName;
    this._mode = mode;
  }

  /**
   * Gets the mode of the mail item.
   *
   * @returns {MailItemMode} The mode of the mail item.
   */
  get mode(): MailItemMode {
    return this._mode;
  }

  /**
   * Get the mail item as an MIME (EML) string.
   *
   * @abstract
   * @returns {Observable<string>} An observable that emits the EML string of the mail item.
   */
  abstract getItemContents(): Observable<string>;

  /**
   * Get all the shared content in the mail item.
   *
   * @abstract
   * @param {function} callback - A callback function to handle the response.
   * @param {Observable<void>} [cancellationObservable] - An optional observable to cancel the operation.
   * @returns {Observable<OutlookSharedContentResult[]>} An observable that emits the shared content of the mail item.
   */
  abstract fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void>;

  /**
   * Add and Remove categories in the mail item.
   *
   * @abstract
   * @param {string[]} remove - The categories to remove.
   * @param {string[]} add - The categories to add.
   * @returns {Observable<void>} An observable that completes when the categories have been replaced.
   */
  abstract replaceCategories(remove: string[], add: string[]): Observable<void>;

  /**
   * Add an attachment to the mail item.
   *
   * @abstract
   * @param {string} base64 - The base64 encoded content of the attachment.
   * @param {string} name - The name of the attachment.
   * @returns {Observable<string>} An observable that emits the ID of the added attachment.
   */
  abstract addAttachment(base64: string, name: string): Observable<string>;
}

/**
 * OfficeJS version of the MailItem class.
 */
export class OutlookMailItem extends MailItem {

  private contextItem: OutlookItemType;

  constructor(
    itemId: string | null,
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    mode: MailItemMode,
    contextItem: OutlookItemType
  ) {
    super(itemId, subject, dateTimeCreated, attachments, mode, createFileNameFromSubject(subject, 'eml'));
    this.contextItem = contextItem;
  }

  getItemContents(): Observable<string> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get EML for compose mode');
    }
    return getAsFileAsyncObservable(this.contextItem).pipe(map((file) => file.value));
  }

  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    const obs: Observable<OutlookSharedContentResult>[] = [
      this.getItemContents().pipe(
        map(content => ({
          detail: { type: 'eml' as const, id: this.itemId! },
          value: { content, format: 'base64' },
          success: true
        })),
        catchError(error => of({
          detail: { type: 'eml' as const, id: this.itemId! },
          success: false,
          error,
          value: undefined
        }))
      )
    ];
    this.attachments.filter((att) => att.attachmentType !== 'cloud').forEach((att) => {
      const detail = { type: 'att' as const, id: att.id };
      const attObs = getAttachmentContentAsyncObservable(this.contextItem, att).pipe(
        map((result) => ({ ...result, detail, success: true })),
        catchError((error) => {
          return of({ detail, success: false, error, value: undefined });
        })
      );
      obs.push(attObs);
    });

    return from(obs).pipe(
      mergeMap((obs) => obs, 2),
      takeUntil(cancellationObservable),
      tap((result) => callback(result)),
      toArray(),
      map(() => {})
    );
  }

  replaceCategories(remove: string[], add: string[]): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot replace categories for compose mode');
    }

    // fetch existing categories
    return createObservableFromAsyncResult<Office.CategoryDetails[]>((callback) => {
      return this.contextItem.categories.getAsync(callback);
    }).pipe(
      mergeMap((categories) => {
        const existing = (categories??[]).map((cat) => cat.displayName);
        // outlook gets very upset if you try to remove categories that aren't there
        // ensure remove are ones that exist and aren't in add
        const filteredRemove = remove.filter(cat => !add.includes(cat) && existing.includes(cat));
        // ensure add aren't already in existing
        const filteredAdd = add.filter(cat => !existing.includes(cat));
        return of(null).pipe(
          // Execute sequentially to ensure remove happens before add
          concatMap(() => this.removeCategories(filteredRemove)),
          delay(300),
          concatMap(() => this.addCategories(filteredAdd)),
        );
      })
    );
  }

  removeCategories(categories: string[]): Observable<void> {
    if (categories.length === 0) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      this.contextItem.categories.removeAsync(categories, callback);
    });
  }

  addCategories(categories: string[]): Observable<void> {
    if (categories.length === 0) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      this.contextItem.categories.addAsync(categories, callback);
    });
  }

  addAttachment(base64: string, name: string): Observable<string> {
    return createObservableFromAsyncResult<string>((callback) => {
      this.contextItem.addFileAttachmentFromBase64Async(
        base64,
        name,
        { isInline: false },
        callback
      );
    });
  }

  static fromOutlookItemType(item: OutlookItemType): Observable<OutlookMailItem> {

    // in read mode its easy to convert the item to MailItem
    if (item.getComposeTypeAsync == undefined) {
      return of(new OutlookMailItem(item.itemId, item.subject, item.dateTimeCreated, item.attachments, 'read', item));
    }

    // otherwise we have to lookup a few calls
    const compItem = item as Office.MessageCompose;
    return forkJoin([
      createObservableFromAsyncResult<string>((callback) => compItem.subject.getAsync(callback)),
      createObservableFromAsyncResult<Office.AttachmentDetailsCompose[]>((callback) => compItem.getAttachmentsAsync(callback)),
    ]).pipe(
      switchMap(([subject, attachments]) => {
        return of(new OutlookMailItem(null, subject, null, attachments as Office.AttachmentDetails[], 'compose', item));
      })
    );
  }

}

const loadItemQueue$ = new BehaviorSubject<void>(undefined);

/**
 * Version of the MailItem class that is loaded from an outlook Mail ID.
 * Outlook has restrictions on concurrently opened items so this delays
 * the loading of the item until it is needed.
 */
export class LoadedOutlookMailItem extends MailItem {
  constructor(itemId: string, subject: string, dateTimeCreated: Date | null, attachments: Office.AttachmentDetails[], mode: MailItemMode) {
    super(itemId, subject, dateTimeCreated, attachments, mode, createFileNameFromSubject(subject, 'eml'));
  }

  /**
   * Executes an operation on a loaded Office item and ensures the item is unloaded after the operation completes.
   *
   * This utility function loads an Office item (e.g., a message in compose or read mode) by its ID, performs the specified
   * operation on the loaded item, and then unloads the item regardless of whether the operation succeeds or fails.
   *
   * @template T The type of the result returned by the operation.
   * @param itemId The ID of the Office item to load.
   * @param operation A function that takes the loaded Office item as an argument and returns an observable of type `T`.
   *                  This function defines the operation to be performed on the loaded item.
   * @param cancellationObservable An optional observable that can be used to cancel the operation.
   * @returns An observable of type `T` that emits the result of the operation. If the operation fails, the error is rethrown
   *          after ensuring the item is unloaded.
   */
  private static withLoadedItem<T>(
    itemId: string,
    operation: (item: Office.LoadedMessageCompose | Office.LoadedMessageRead) => Observable<T>,
    cancellationObservable: Observable<void> = NEVER
  ): Observable<T> {
    // Queue this operation by using the queue subject
    return loadItemQueue$.pipe(
      // Cancel the operation if the cancellation observable emits
      takeUntil(cancellationObservable),
      // Delay each operation slightly to ensure proper queuing
      delay(10),
      // Only take one value to release the queue for the next operation
      take(1),
      // Load the item and perform the operation
      mergeMap(() => {
        return createObservableFromAsyncResult<Office.LoadedMessageCompose | Office.LoadedMessageRead>((callback) =>
          Office.context.mailbox.loadItemByIdAsync(itemId, callback)
        )
      }),
      mergeMap((item) =>
        operation(item).pipe(
          // Unload the item after the operation completes
          finalize(() => {
            item.unloadAsync(() => {
              console.log('Unloaded item');
              // Signal that the next operation can proceed
              loadItemQueue$.next(undefined);
            });
          }),
          // Handle errors while still ensuring unload
          catchError((error) => {
            return throwError(() => error);
          })
        )
      )
    );
  }

  static fromOutlookItemId(itemId: string): Observable<LoadedOutlookMailItem> {
    return this.withLoadedItem(itemId, (item) => {
      if ('dateTimeCreated' in item) {
        const mailItem = new LoadedOutlookMailItem(itemId, item.subject, item.dateTimeCreated, item.attachments, 'read');
        return of(mailItem);
      } else {
        const composeItem = item as Office.LoadedMessageCompose;
        return forkJoin([
          createObservableFromAsyncResult<string>((callback) => composeItem.subject.getAsync(callback)),
          createObservableFromAsyncResult<Office.AttachmentDetailsCompose[]>((callback) => composeItem.getAttachmentsAsync(callback)),
        ]).pipe(
          map(([subject, attachments]) => {
            return new LoadedOutlookMailItem(itemId, subject, null, attachments as Office.AttachmentDetails[], 'compose');
          })
        );
      }
    });
  }

  getItemContents(): Observable<string> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get EML for compose mode');
    }
    return LoadedOutlookMailItem.withLoadedItem(this.itemId!, (item) => {
      return this._getItemContents(item as Office.LoadedMessageRead);
    });
  }

  private _getItemContents(item: Office.LoadedMessageRead): Observable<string> {
    return getAsFileAsyncObservable(item).pipe(map((file) => file.value));
  }

  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get attachments for compose mode');
    }
    return LoadedOutlookMailItem.withLoadedItem(this.itemId!, (item) => {
      const obs: Observable<OutlookSharedContentResult>[] = [
        this._getItemContents(item as Office.LoadedMessageRead).pipe(
          map(content => ({
            detail: { type: 'eml' as const, id: this.itemId! },
            value: { content, format: 'base64' },
            success: true
          })),
          catchError(error => of({
            detail: { type: 'eml' as const, id: this.itemId! },
            success: false,
            error,
            value: undefined
          }))
        )
      ];
      this.attachments.filter((att) => att.attachmentType !== 'cloud').forEach((att) => {
        const detail = { type: 'att' as const, id: att.id };
        const attObs = getAttachmentContentAsyncObservable(item as Office.LoadedMessageRead, att).pipe(
          map((result) => ({ ...result, detail, success: true })),
          catchError((error) => {
            return of({ detail, success: false, error, value: undefined });
          })
        );
        obs.push(attObs);
      });
      let count = 0;
      return from(obs).pipe(
        mergeMap((o) => o.pipe(tap(() => console.log(`Obtained content ${++count} of ${obs.length}`))), 2),
        takeUntil(cancellationObservable),
        tap((result) => callback(result)),
        toArray(),
        map(() => {})
      );
    }, cancellationObservable);
  }

  replaceCategories(remove: string[], add: string[]): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot replace categories for compose mode');
    }

    return LoadedOutlookMailItem.withLoadedItem(this.itemId!, (item) => {
      // fetch existing categories
      return createObservableFromAsyncResult<Office.CategoryDetails[]>((callback) => {
        return item.categories.getAsync(callback);
      }).pipe(
        mergeMap((categories) => {
          const existing = (categories??[]).map((cat) => cat.displayName);
          // outlook gets very upset if you try to remove categories that aren't there
          // ensure remove are ones that exist and aren't in add
          const filteredRemove = remove.filter(cat => !add.includes(cat) && existing.includes(cat));
          // ensure add aren't already in existing
          const filteredAdd = add.filter(cat => !existing.includes(cat));
          return of(null).pipe(
            // Execute sequentially to ensure remove happens before add
            concatMap(() => this.removeCategories(filteredRemove, item)),
            delay(300),
            concatMap(() => this.addCategories(filteredAdd, item)),
          );
        })
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addAttachment(base64: string, name: string): Observable<string> {
    throw new Error('Method not implemented.');
  }

  private removeCategories(categories: string[], item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Observable<void> {
    if (!categories?.length) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      item.categories.removeAsync(categories, callback);
    });
  }

  private addCategories(categories: string[], item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Observable<void> {
    if (!categories?.length) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      item.categories.addAsync(categories, callback);
    });
  }
}

/**
 * Abstract base class for mail items loaded from files (MSG, EML)
 */
export abstract class FileBasedMailItem extends MailItem {
  static lastFileNdx = 5000;

  protected constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    fileName: string,
  ) {
    super(`fb-${FileBasedMailItem.lastFileNdx++}`, subject, dateTimeCreated, attachments, 'read', fileName);
  }

  static fromFile(file: File): Observable<FileBasedMailItem> {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'eml') {
      return EmlMailItem.fromEmlFile(file);
    } else if (ext === 'msg') {
      return MsgMailItem.fromMsgFile(file);
    } else {
      return new Observable((observer) => {
        observer.error(new Error('Unsupported file type'));
      });
    }
  }

  abstract getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult>;

  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    const obs: Observable<OutlookSharedContentResult>[] = [
      this.getItemContents().pipe(
        map(content => ({
          detail: { type: 'eml' as const, id: this.itemId! },
          value: { content, format: 'base64' },
          success: true
        })),
        catchError(error => of({
          detail: { type: 'eml' as const, id: this.itemId! },
          success: false,
          error,
          value: undefined
        }))
      )
    ];
    this.attachments.filter((att) => att.attachmentType !== 'cloud').forEach((att) => {
      const detail = { type: 'att' as const, id: att.id };
      const attObs = this.getAttachment(att).pipe(
        map((result) => ({ ...result, detail, success: true })),
        catchError((error) => {
          return of({ detail, success: false, error, value: undefined });
        })
      );
      obs.push(attObs);
    });
    return from(obs).pipe(
      mergeMap((o) => o.pipe(tap(() => console.log(`Obtained content`))), 2),
      takeUntil(cancellationObservable),
      tap((result) => callback(result)),
      toArray(),
      map(() => {})
    );
  }

  replaceCategories(_remove: string[], _add: string[]): Observable<void> {
    return of(undefined);
  }

  addAttachment(_base64: string, _name: string): Observable<string> {
    return new Observable((observer) => {
      observer.error(new Error('Cannot add attachments to file-based mail items'));
    });
  }
}

/**
 * Represents a mail item loaded from an EML file.
 */
export class EmlMailItem extends FileBasedMailItem {

  private buffer: Buffer;
  private emlData: ReadedEmlJson;

  constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    buffer: Buffer,
    emlData: ReadedEmlJson,
    fileName: string,
  ) {
    super(subject, dateTimeCreated, attachments, fileName);
    this.buffer = buffer;
    this.emlData = emlData;
  }

  static fromNameAndBuffer(fileName: string, buffer: Buffer): Observable<EmlMailItem> {
    return new Observable((observer) => {
      try {
        const resp = readEml(buffer.toString());
        if (typeof resp === 'string' || !('date' in resp)) {
          console.error("Failed to parse mail EML. Error:", resp);
          observer.error(new Error("Failed to parse mail EML"));
          return;
        }

        const eml = resp as ReadedEmlJson;
        const atts = (eml.attachments ?? []).map((att) => {
          let name = att.name;
          if (!name) return null;

          if (/^message\/rfc822/.test(att.contentType)) {
            // If the attachment is an RFC822 message, we have to parse it to get the name
            const data = typeof att.data === 'string' ? att.data : decode(att.data).toString();
            if (!att.data64) {
              // suppliment the data64 if not present
              att.data = '';
              att.data64 = Buffer.from(data).toString('base64');
            }
            const resp = readEml(data, { headersOnly: true });
            if (typeof resp === 'object' && 'subject' in resp) {
              const attEml = resp as ReadedEmlJson;
              name = createFileNameFromSubject(attEml.subject || 'Unknown', 'eml');
              att.name = name;
            }
          }

          // emls do not have a unique ID for attachments, so we make up one and record the index offset
          return {
            id: crypto.randomUUID(),
            name,
            size: att.data?.length || 0,
            attachmentType: "file",
            contentType: att.contentType || '',
            isInline: !!att.inline,
          } as Office.AttachmentDetails;
        }).filter((att) => att !== null) as Office.AttachmentDetails[];

        const mailItem = new EmlMailItem(
          eml.subject || '',
          new Date(eml.date || ''),
          atts,
          buffer,
          eml,
          fileName,
        );

        observer.next(mailItem);
        observer.complete();
      } catch (err) {
        observer.error(new Error('Failed to parse EML content: ' + getErrorMessage(err)));
      }
    });
  }

  static fromEmlFile(file: File): Observable<EmlMailItem> {
    return convertFileToBuffer(file).pipe(
      mergeMap(buffer => EmlMailItem.fromNameAndBuffer(file.name, buffer)),
    );
  }

  getItemContents(): Observable<string> {
    return of(this.buffer.toString('base64'));
  }

  getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult> {
    return of(attachment).pipe(
      map((att) => {
        const emlAtt = this.emlData.attachments?.find(a => a.id === att.id || a.name === att.name);
        if (!emlAtt || !emlAtt.data64) {
          return {
            detail: att,
            success: false,
            error: new Error('Attachment not found or empty'),
            value: undefined
          };
        }
        return {
          detail: att,
          success: true,
          value: {
            content: emlAtt.data64,
            contentType: emlAtt.contentType,
            format: "base64"
          }
        };
      })
    );
  }
}

/**
 * Represents a mail item loaded from an MSG file.
 */
export class MsgMailItem extends FileBasedMailItem {
  private msgContent: Buffer;
  private msgReader: MsgReader;
  private attachmentIndex: { [key: string]: number };

  constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    msgContent: Buffer,
    msgReader: MsgReader,
    fileName: string,
    attachmentIndex: { [key: string]: number }
  ) {
    super(subject, dateTimeCreated, attachments, fileName);
    this.msgContent = msgContent;
    this.msgReader = msgReader;
    this.fileName = fileName;
    this.attachmentIndex = attachmentIndex;
  }

  static fromMsgFile(file: File): Observable<MsgMailItem> {
    return convertFileToBuffer(file).pipe(
      mergeMap(buffer => MsgMailItem.fromNameAndBuffer(file.name, buffer))
    );
  }

  static fromNameAndBuffer(fileName: string, buffer: Buffer): Observable<MsgMailItem> {
    return new Observable((observer) => {
      try {
        const msgReader = new MsgReader(buffer);
        const msgData = msgReader.getFileData();
        const atts = msgData.attachments || [];
        const attachmentIndex: { [key: string]: number } = {};

        const mailItem = new MsgMailItem(
          stripTrim(msgData.subject) || '',
          new Date(msgData.creationTime || ''),
          atts.map((att, index) => {
            let name = stripTrim(att.fileName) || 'Unknown';
            if (att.innerMsgContent && att.innerMsgContentFields) {
              const dt = att.innerMsgContentFields?.dataType?.trim() || 'attachment';
              if (dt === 'msg') {
                name = createFileNameFromSubject(stripTrim(att.innerMsgContentFields.subject) || 'Unknown', 'msg');
              } else if (dt === 'attachment') {
                name = att.innerMsgContentFields?.fileName || 'Unknown';
              }
            }

            const id = crypto.randomUUID();
            attachmentIndex[id] = index;

            return {
              id,
              name,
              size: att.contentLength || 0,
              attachmentType: "file",
              contentType: att.attachMimeTag || '',
            };
          }) as Office.AttachmentDetails[],
          buffer,
          msgReader,
          fileName,
          attachmentIndex
        );

        observer.next(mailItem);
        observer.complete();
      } catch (err) {
        observer.error(new Error('Failed to parse MSG content: ' + getErrorMessage(err)));
      }
    });
  }

  getItemContents(): Observable<string> {
    return of(this.msgContent.toString('base64'));
  }

  getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult> {
    return of(attachment).pipe(
      map((att) => {
        // Find attachment by converting ID back to number for comparison
        const index = this.attachmentIndex[att.id];
        const attachmentData = this.msgReader.getAttachment(index);
        if (!attachmentData) {
          return {
            detail: att,
            success: false,
            error: new Error('Attachment not found or empty'),
            value: undefined
          };
        }

        // Convert Uint8Array to base64
        const content = Buffer.from(attachmentData.content).toString('base64');

        return {
          detail: att,
          success: true,
          value: {
            content,
            contentType: '',
            format: "base64"
          }
        };
      }),
    );
  }
}

// Error handling helper
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
function createFileNameFromSubject(subject: string, ext: 'msg' | 'eml'): string {
  return cleanAndTruncateFileName(`${subject}.${ext}`, MAX_FILENAME_LENGTH, false, true, false)
}
function stripTrim(str: string | undefined): string | undefined {
  // eslint-disable-next-line no-control-regex
  return str?.replace(/[\x00-\x1F\x7F]/g, '').trim();
}
